import { action, asyncAction } from '@play-co/replicant';
import createActions from '../config/actions/createActions';
import { ReferralRewardType, SocialSubFeature } from './social.ruleset';
import {
  getExcludedFriendsForInvite,
  getInviteReward,
  getPendingReferralRewards,
  isInviteChallengeComplete,
} from './social.getters';
import {
  claimWallpaperChallengeReward,
  recordInvites,
  setAllReferralRewardsClaimed,
} from './social.modifiers';
import * as Inventory from '../inventory';
import { assertNever } from '../utils';
import { getBaseMiniAppURL } from '../utils/payload';
import { isFirstSession } from '../utils/platform';
import { NativeShareCreativeID } from 'src/creatives/share';
import * as Friends from '../friends';
import { setPromoSeen } from '../promo/promo.modifiers';
import { isBuruFriend, wasUnfriended } from '../friends/friends.getters';

export const socialActions = createActions({
  recordInvite: action(
    (
      state,
      args: { friendIds: string[]; canAttemptPayments: boolean },
      api,
    ) => {
      const now = api.date.now();
      let friendIds = args.friendIds.slice();
      const excludedFriends = getExcludedFriendsForInvite(
        state,
        friendIds,
        now,
        args.canAttemptPayments,
      );
      if (excludedFriends.length > 0) {
        api.reportError(
          new Error(`Attempting to record invite for excluded friends`),
          { excludedFriends },
        );

        // Remove any already invited friends to prevent exploit
        friendIds = friendIds.filter((id) => !excludedFriends.includes(id));
      }

      const wasInviteChallengeComplete = isInviteChallengeComplete(state);

      recordInvites(state, friendIds, now);

      if (isInviteChallengeComplete(state) && !wasInviteChallengeComplete) {
        claimWallpaperChallengeReward(state, api);
      }
    },
  ),

  claimInviteRewards: action((state, subFeature: SocialSubFeature, api) => {
    const claimedRewardCount = setAllReferralRewardsClaimed(state);

    const inviteReward = getInviteReward(state, subFeature);
    const amount = claimedRewardCount * inviteReward.value;

    // Give energy or gems depends on feature type
    const type = inviteReward.type;
    switch (type) {
      case 'energy':
        Inventory.modifiers.addEnergy(
          state,
          amount,
          api.date.now(),
          { feature: 'invite_reward' },
          api,
        );
        break;
      case 'gems':
        Inventory.modifiers.addFreeGems(
          state,
          amount,
          { feature: 'invite_reward' },
          api,
        );
        break;
      case 'moves':
        // Handled on the front-end in session
        break;
      default:
        assertNever(type);
    }

    return { amount, ...inviteReward };
  }),

  /**
   * Invited user entered to game first time
   */
  referralFirstLogin: action((state, args: { inviter: string }, api) => {
    // First entry referral sequence only available for new users
    if (!isFirstSession(state)) return;

    // Check if same user
    const { inviter } = args;
    if (inviter === state.id) {
      throw new Error('Can`t send invite reward to self');
    }

    // Automatically add inviter as a friend for enabled in-game friends feature flag,
    // regardless of whether the user is a new user or not.
    Friends.modifiers.addFriend(state, inviter, api.date.now());

    // User already finished first enter flow
    const { referral } = state.social;
    if (referral.joinCompleteAt !== 0) return;

    // Save referral information
    const now = api.date.now();
    referral.invitedBy = inviter;
    referral.joinCompleteAt = now;

    // Send a message to the inviter to notify that his invitee has joined
    api.postMessage.referralFriendJoined(inviter, {});
  }),

  /**
   * Invited existing user entered to game
   */
  referralSubsequentLogin: action((state, args: { inviter: string }, api) => {
    // Existing user referral sequence not available for new users
    if (isFirstSession(state)) return;

    // Check if same user
    const { inviter } = args;
    if (inviter === state.id) {
      throw new Error('Can`t send invite reward to self');
    }

    // Skip if already friends
    if (isBuruFriend(state, inviter)) return;

    // Skip if it's an unfriended user
    if (wasUnfriended(state, inviter)) return;

    // Otherwise, add inviter as a friend
    Friends.modifiers.addFriend(state, inviter, api.date.now());

    // Send a message to the inviter to reciprocate adding the invitee as a friend
    api.postMessage.referralFriendAdded(inviter, {});
  }),

  /**
   * Claim success joined reward
   */
  referralClaimSuccessJoinedReward: action((state, _: void, api) => {
    // No rewards
    if (state.social.referral.pendingRewards.length === 0)
      throw new Error('No available rewards found');

    // Give rewards
    const rewards = getPendingReferralRewards(state);
    Object.entries(rewards).forEach(([type, value]) => {
      const rewardType = type as ReferralRewardType;
      switch (rewardType) {
        case 'gems':
          Inventory.modifiers.addFreeGems(
            state,
            value,
            {
              feature: 'invite_reward',
            },
            api,
          );
          break;
        default:
          assertNever(rewardType);
      }
    });

    // Clear rewards
    state.social.referral.pendingRewards = [];

    // For enabled in-game friends feature flag, set referral rewards collection on cooldown.
    setPromoSeen(
      state,
      'inGameFriendReferralRewardsCollection',
      api.date.now(),
    );
  }),

  clearReferredVisitable: action((state) => {
    delete state.social.referral.visitableFriend;
  }),

  generateWebShareLink: asyncAction(
    async (
      state,
      args: {
        payload: object;
        assetId?: NativeShareCreativeID;
        image: string;
        title: string;
        description?: string;
        buildNo?: string;
        hash: string;
      },
      api,
    ) => {
      const { payload, ...opts } = args;
      const baseURL = getBaseMiniAppURL();

      const url = `${baseURL}?payload=${encodeURIComponent(
        JSON.stringify(payload),
      )}`;

      try {
        const { linkId } = await api.asyncGetters.getShortLink({
          url,
          data: opts,
        });

        if (!linkId) {
          return null;
        }

        // Save generated link
        state.social.shareLink = {
          id: linkId,
          hash: args.hash,
          createdAt: api.date.now(),
        };

        return linkId;
      } catch (error) {
        // Capture the error
        api.reportError(new Error('generateWebShareLinkFailed'), {
          error,
        });

        // Return null on failure
        return null;
      }
    },
  ),
});
